import useCameraFleetFlag from '@cobliteam/hooks/src/useCamera/useCameraFleetFlag';
import useFeatureFlags, { type FeatureFlags, featureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
import Switch from '@cobliteam/ui/components/Switch';
import Typography from '@cobliteam/ui/components/Typography';
import Input from '@cobliteam/ui/form/Input';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useMemo, useState } from 'react';
import DevToolsPanel from '../DevToolsPanel';

const FlagValue = ({ value }: { value: unknown }) => {
  if (typeof value === 'boolean') {
    return (
      <span className={cn('rounded-full px-2 py-0.5 font-medium font-mono text-[11px]', value ? 'bg-success-3 text-success-11' : 'bg-gray-3 text-gray-11')}>{String(value)}</span>
    );
  }

  const display = typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);

  return (
    <span className='truncate font-mono text-gray-11 text-xs' title={display}>
      {display}
    </span>
  );
};

/**
 * The catalog list is read-only: values are resolved from LaunchDarkly, so the
 * panel's job is telling you what this session actually sees. The camera package
 * is the one writable override, because it's swapped in the query cache rather
 * than in LaunchDarkly.
 */
const FlagsData = () => {
  const [search, setSearch] = useState('');

  const flags = useFeatureFlags();
  const { useFleetPremiumPackageFlag, setFleetPremiumPackageFlag } = useCameraFleetFlag();
  const { data: isPremium } = useFleetPremiumPackageFlag();

  const totalFlags = Object.keys(featureFlags).length;

  const visibleFlags = useMemo(() => {
    const term = search.trim().toLowerCase();

    return (Object.keys(featureFlags) as Array<keyof FeatureFlags>)
      .filter((key) => !term || key.toLowerCase().includes(term))
      .sort()
      .map((key) => ({ key, value: flags[key] as unknown }));
  }, [flags, search]);

  return (
    <DevToolsPanel
      title='Flags'
      description='Valores que esta sessão está vendo, resolvidos do LaunchDarkly. A lista é somente leitura; o override abaixo vale só nesta aba.'
      toolbar={
        <>
          <Input
            size='sm'
            aria-label='Filtrar flags'
            placeholder='Filtrar flags...'
            startIcon={<Icon name='search' size='sm' />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Typography variant='caption-xs' color='secondary' className='shrink-0 whitespace-nowrap'>
            {visibleFlags.length} de {totalFlags}
          </Typography>
        </>
      }
    >
      <div className='flex items-center justify-between gap-4 rounded-lg border border-gray-6 p-3'>
        <div className='flex flex-col gap-0.5'>
          <Label htmlFor='flag-premium'>Pacote Premium (override)</Label>
          <Typography variant='caption-xs' color='secondary'>
            Câmera · FLEET_PRODUCTS_FLAG — recarregar volta ao valor real da frota
          </Typography>
        </div>
        <Switch id='flag-premium' checked={isPremium ?? false} onCheckedChange={setFleetPremiumPackageFlag} />
      </div>

      {visibleFlags.length === 0 ? (
        <Typography variant='body' color='secondary'>
          Nenhuma flag corresponde a “{search}”.
        </Typography>
      ) : (
        <dl className='max-h-64 divide-y divide-gray-4 overflow-y-auto rounded-lg border border-gray-6'>
          {visibleFlags.map(({ key, value }) => (
            <div key={key} className='flex items-center justify-between gap-4 px-3 py-1.5'>
              <dt className='min-w-0'>
                <Typography variant='caption-xs' className='truncate font-mono'>
                  {key}
                </Typography>
              </dt>
              <dd className='flex shrink-0 items-center'>
                <FlagValue value={value} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </DevToolsPanel>
  );
};

export default FlagsData;
